import React from 'react';

// Selector for all focusable menu item types
export const MENU_ITEM_SELECTOR = '[role="menuitemcheckbox"], [role="menuitemradio"], .bs4-dropdown-item, .accordion-header';

// Natural focusable elements
export const NATURALLY_FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [role="button"]';

/**
 * Filters menu items to only those that are truly focusable and visible.
 * 
 * @param items - Array of HTMLElements to filter
 * @returns Filtered array of focusable items
 */
export const filterFocusableMenuItems = (items: HTMLElement[]): HTMLElement[] => {
  return items.filter(item => {
    // Check if item is visible (not in a collapsed accordion)
    const accordionCollapse = item.closest('.accordion-collapse');
    if (accordionCollapse && !accordionCollapse.classList.contains('expanded')) {
      return false; // Skip items in collapsed accordions
    }
    
    // Special case: items inside accordion content are always treated as top-level
    const isInAccordion = !!item.closest('.accordion-content');
    if (isInAccordion) {
      const tabIndex = item.tabIndex;
      return tabIndex >= 0 || item.matches(NATURALLY_FOCUSABLE_SELECTOR);
    }
    
    // For non-accordion items, check if they're nested inside another menu item
    const parent = item.parentElement?.closest(MENU_ITEM_SELECTOR);
    const isNested = parent && parent !== item;
    if (isNested) return false;
    
    const tabIndex = item.tabIndex;
    return tabIndex >= 0 || item.matches(NATURALLY_FOCUSABLE_SELECTOR);
  });
};

/**
 * Custom hook for handling keyboard navigation in dropdown menu items.
 * Provides arrow key navigation (up/down) between focusable menu items.
 *
 * @param itemRef - Reference to the menu item element
 * @returns Handler function for arrow key events
 */
const useMenuItemKeyboardNav = (itemRef: React.RefObject<HTMLElement>) => {
  const handleArrowKeys = (e: React.KeyboardEvent) => {
    if (!(e.key === 'ArrowDown' || e.key === 'Down' || e.key === 'ArrowUp' || e.key === 'Up')) {
      return false;
    }

    const menu = itemRef.current?.closest('.bs4-dropdown-menu');
    if (!menu) return false;

    // Query for menu items with roles, then filter to only truly focusable elements
    const allItems = Array.from(
      menu.querySelectorAll(MENU_ITEM_SELECTOR)
    ) as HTMLElement[];

    const focusableItems = filterFocusableMenuItems(allItems);

    if (focusableItems.length === 0) return false;

    const currentIndex = focusableItems.indexOf(itemRef.current!);
    if (currentIndex === -1) return false;

    // Calculate next/previous index with wrapping
    let targetIndex: number;
    if (e.key === 'ArrowDown' || e.key === 'Down') {
      targetIndex = currentIndex < focusableItems.length - 1 ? currentIndex + 1 : 0;
    } else {
      targetIndex = currentIndex > 0 ? currentIndex - 1 : focusableItems.length - 1;
    }

    const targetElement = focusableItems[targetIndex];
    if (!targetElement) return false;

    // Only prevent default and move focus if we have a valid target
    e.preventDefault();
    e.stopPropagation();
    targetElement.focus();

    return true;
  };

  return handleArrowKeys;
};

export default useMenuItemKeyboardNav;
